// src/components/admin/AITagSuggest.tsx
import React, { useState, useEffect, useRef } from 'react';
import { Sparkles, X, Plus, AlertCircle, Search } from 'lucide-react';
import debounce from 'lodash/debounce';
import { adminAPI } from '../../api/admin';
import { categoriesAPI } from '../../api/categories';
import { useDebounce } from '../../hooks/useDebounce';
import { compareTags } from '../../utils/tagUtils';

interface AITagSuggestProps {
    value: string[];
    onChange: (tags: string[]) => void;
    placeholder?: string;
    label: string;
    type: 'categories' | 'keywords';
    productTitle?: string;
    productDescription?: string;
    productSpecs?: Record<string, any>;
    isLoading?: Boolean;
    maxTags: number;
    autoSuggestEnabled?: boolean;
    disabled?: boolean;
    error?: string | null;
    name?: string;
    isReviewMode?: boolean;
    previousValue?: string[]
    // availableCategories?: string[];
    // showCategoryDropdown?: boolean;
}

const AITagSuggest: React.FC<AITagSuggestProps> = ({
    value,
    onChange,
    placeholder,
    label,
    type,
    productTitle,
    productDescription,
    productSpecs,
    isLoading,
    maxTags,
    autoSuggestEnabled = false,
    disabled = false,
    error,
    name,
    isReviewMode,
    previousValue,
    // availableCategories = [],
    // showCategoryDropdown = false,
}) => {
    const [input, setInput] = useState('');
    const [suggestions, setSuggestions] = useState<string[]>([]);
    const [loading, setLoading] = useState(false);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);
    const [initialized, setInitialized] = useState(false);

    const [showCategoryList, setShowCategoryList] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);
    const suggestionRef = useRef<HTMLDivElement>(null);
    const [categoryOptions, setCategoryOptions] = useState<string[]>([]);
    const [isSearchingCategories, setIsSearchingCategories] = useState(false);
    const searchTimeout = useRef<NodeJS.Timeout | null>(null);
    const debouncedSearch = useDebounce(input, 300);
    const [popularCategories, setPopularCategories] = useState<string[]>([]);
    const { existing, added, removed } = isReviewMode && previousValue
        ? compareTags(previousValue, value)
        : { existing: value, added: [], removed: [] };

    const hasChanges = added.length > 0 || removed.length > 0;

    // Close suggestions when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target as Node)) {
                setShowSuggestions(false);
                setShowCategoryList(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Only fetch suggestions after initial render, and don't overwrite existing tags
    useEffect(() => {
        // Don't fetch immediately - wait for user interaction or debounce
        const timer = setTimeout(() => {
            if (initialized && (productTitle || productDescription || productSpecs)) {
                if (!autoSuggestEnabled) return;

                fetchSuggestions();
            }
        }, 1000);

        return () => clearTimeout(timer);
    }, [productTitle, productDescription, productSpecs, initialized]);
    // Set initialized after component mounts
    useEffect(() => {
        setInitialized(true);
    }, []);

    // Effect to search when debounced input changes
    useEffect(() => {
        if (type === 'categories' && debouncedSearch && debouncedSearch.length >= 2) {
            searchCategories(debouncedSearch);
        } else if (type === 'categories') {
            setCategoryOptions([]);
        }
    }, [debouncedSearch, type]);

    // Fetch popular categories on mount
    useEffect(() => {
        if (type === 'categories') {
            fetchPopularCategories();
        }
    }, [type]);

    const searchCategories = async (query: string) => {
        if (!query || query.length < 2) {
            setCategoryOptions([]);
            return;
        }

        setIsSearchingCategories(true);
        try {
            const response = await categoriesAPI.searchCategories(query, 15);
            if (!response.error) {
                setCategoryOptions(response.data.map((c: any) => c.name));
            }
        } catch (error) {
            console.error('Failed to search categories:', error);
        } finally {
            setIsSearchingCategories(false);
        }
    };

    const fetchPopularCategories = async () => {
        try {
            const response = await categoriesAPI.getPopularCategories(20);
            if (!response.error) {
                setPopularCategories(response.data.map((c: any) => c.name));
                // If no input, show popular categories
                if (!input) {
                    setCategoryOptions(popularCategories);
                    setShowCategoryList(true);
                }
            }
        } catch (error) {
            console.error('Failed to fetch popular categories:', error);
        }
    };

    // Filter categories based on input
    // const filteredCategories = availableCategories.filter(
    //     cat => cat.toLowerCase().includes(input.toLowerCase()) && !value.includes(cat)
    // );

    const fetchSuggestions = debounce(async () => {
        if (!productTitle && !productDescription && !productSpecs) {
            setSuggestions([]);
            return;
        }

        setLoading(true);
        try {
            const response = await adminAPI.suggestTags({
                title: productTitle || '',
                description: productDescription || '',
                specs: productSpecs || {},
                type: type,
                existing_tags: value
            });

            // Check if the response has data (success) or error
            if (response.data) {
                // Success - access suggestions from response.data.data
                const suggestions = response.data.suggestions || [];
                const uniqueSuggestions = [...new Set(suggestions)];
                setSuggestions(uniqueSuggestions);
            } else if (response.error) {
                // Handle error
                console.error('Failed to fetch suggestions:', response.error);
                // Optionally show error to user
            }
        } catch (error) {
            console.error('Failed to fetch suggestions:', error);
        } finally {
            setLoading(false);
        }
    }, 500);

    const handleAdd = (tag: string) => {
        if (type === 'keywords' && tag.includes(',')) {
            const parts = tag
                .split(',')
                .map(t => t.trim())
                .filter(t => t.length > 0);

            // dedupe against existing and within the batch, respect maxTags
            const next = [...value];
            for (const p of parts) {
                if (next.length >= maxTags) break;
                if (!next.includes(p)) next.push(p);
            }
            onChange(next);
            setInput('');
            setShowSuggestions(false);
            setShowCategoryList(false);
            return;
        }

        // existing single-tag logic
        if (!value.includes(tag)) {
            onChange([...value, tag]);
            setSuggestions(prev => prev.filter(t => t !== tag));
        }
        setInput('');
        setShowSuggestions(false);
        setShowCategoryList(false);
    };

    const handleRemove = (tag: string) => {
        onChange(value.filter(t => t !== tag));
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && input) {
            e.preventDefault();

            if (type === 'categories') {
                // For categories: select first dropdown item if available
                if (categoryOptions.length > 0) {
                    const firstCategory = categoryOptions[0];
                    if (firstCategory && !value.includes(firstCategory)) {
                        handleAdd(firstCategory);
                        setInput('');
                        setShowCategoryList(false);
                    }
                }
                // Do nothing if no category selected (prevent custom category creation)
            } else {
                // For keywords: allow manual addition
                if (input.trim() && !value.includes(input.trim())) {
                    handleAdd(input);
                }
            }
        }
    };

    return (
        <div className="space-y-2" ref={wrapperRef} data-field={name || label}>
            <label className="block text-sm font-medium text-gray-700">
                {label} ({value.length}/{maxTags})
                {isLoading && <Sparkles size={14} className="inline ml-2 text-purple-500 animate-pulse" />}
            </label>
            {/* Tags */}
            {/* Tags - Modified for Review Mode */}
            <div className="flex flex-wrap gap-2 mb-2">
                {isReviewMode && hasChanges ? (
                    <>
                        {/* Legend */}
                        <div className="w-full flex items-center gap-3 text-xs mb-1">
                            <span className="text-gray-500">Legend:</span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 bg-blue-100 border border-blue-300 rounded-sm" />
                                <span className="text-gray-600">Existing</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 bg-green-100 border border-green-300 rounded-sm" />
                                <span className="text-gray-600">Added</span>
                            </span>
                            <span className="flex items-center gap-1.5">
                                <span className="w-3 h-3 bg-red-100 border border-red-300 rounded-sm" />
                                <span className="text-gray-600">Removed</span>
                            </span>
                        </div>

                        {/* Existing Tags (Blue) */}
                        {existing.map((tag, index) => (
                            <span
                                key={`existing-${index}`}
                                className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm"
                            >
                                {tag}
                            </span>
                        ))}

                        {/* Added Tags (Green) */}
                        {added.map((tag, index) => (
                            <span
                                key={`added-${index}`}
                                className="px-3 py-1.5 bg-green-100 text-green-800 rounded-full text-sm"
                            >
                                + {tag}
                            </span>
                        ))}

                        {/* Removed Tags (Red with strikethrough) */}
                        {removed.map((tag, index) => (
                            <span
                                key={`removed-${index}`}
                                className="px-3 py-1.5 bg-red-100 text-red-800 rounded-full text-sm line-through"
                            >
                                {tag}
                            </span>
                        ))}

                        {/* Summary */}
                        {/* <div className="w-full space-y-0.5 text-xs mt-1">
                            {added.length > 0 && (
                                <p className="text-green-600">
                                    Added {type === 'keywords' ? 'keywords' : 'categories'}: {added.join(', ')}
                                </p>
                            )}
                            {removed.length > 0 && (
                                <p className="text-red-600">
                                    Removed {type === 'keywords' ? 'keywords' : 'categories'}: {removed.join(', ')}
                                </p>
                            )}
                        </div> */}
                    </>
                ) : (
                    // Edit Mode: Normal display (no colors)
                    value.map((tag, index) => (
                        <span
                            key={`${type}-${tag}-${index}`}
                            className="px-3 py-1.5 bg-blue-100 text-blue-800 rounded-full text-sm flex items-center group"
                        >
                            {tag}
                            {!disabled && (
                                <button
                                    type="button"
                                    onClick={() => handleRemove(tag)}
                                    className="ml-2 text-blue-600 hover:text-blue-800"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </span>
                    ))
                )}
            </div>

            {/* Input with Suggestions */}
            <div className="relative">
                {!disabled && (
                    <div className={`flex gap-2 ${error ? 'border-red-500' : ''}`}>
                        <input
                            type="text"
                            value={input}
                            onChange={(e) => {
                                setInput(e.target.value);
                                if (type === 'categories') {
                                    setShowCategoryList(true);
                                    setShowSuggestions(false);
                                    if (searchTimeout.current) clearTimeout(searchTimeout.current);
                                    searchTimeout.current = setTimeout(() => {
                                        searchCategories(input);
                                    }, 300);
                                } else {
                                    setShowSuggestions(true);
                                }

                            }}
                            onFocus={() => {
                                if (type === 'categories') {
                                    if (!input && popularCategories.length > 0) {
                                        setCategoryOptions(popularCategories);
                                        setShowCategoryList(true);
                                    } else {
                                        setShowCategoryList(true);
                                    }
                                } else {
                                    setShowSuggestions(true);
                                }
                            }}
                            onKeyPress={handleKeyPress}
                            placeholder={placeholder}
                            disabled={disabled || value.length >= maxTags || isReviewMode}
                            className={`flex-1 px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${error ? 'border-red-500 bg-red-50' : 'border-gray-300'
                                }`}
                        />
                        {!isReviewMode &&
                            <button
                                type="button"
                                onClick={() => {
                                    if (type === 'categories') {
                                        // ✅ Show category dropdown with popular categories
                                        console.log(showCategoryList, categoryOptions, categoryOptions.length)
                                        setShowCategoryList(true);
                                        // If no input, show popular categories
                                        if (!input) {
                                            fetchPopularCategories();
                                        }
                                    } else {
                                        handleAdd(input);
                                    }
                                }}
                                disabled={type === 'categories' ? false : !input}
                                className={`px-4 py-2 text-gray-700 rounded-lg transition-colors ${type === 'categories'
                                    ? 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                                    : 'bg-gray-100 hover:bg-gray-200 disabled:opacity-50'
                                    }`}
                                title={type === 'categories' ? 'Browse categories' : 'Add keyword'}
                            >
                                {type === 'categories' ? <Search size={20} /> : <Plus size={20} />}
                            </button>
                        }
                    </div>
                )}

                {isLoading && (
                    <div className="text-xs text-gray-400 flex items-center mt-1">
                        <Sparkles size={12} className="mr-1 text-purple-500 animate-spin" />
                        Generating suggestions...
                    </div>
                )}

                {error && (
                    <p className="mt-1 text-sm text-red-600 flex items-center gap-1">
                        <AlertCircle className="h-4 w-4" />
                        {error}
                    </p>
                )}

                {/* Category Dropdown (only for categories type) */}
                {type === 'categories' && showCategoryList && categoryOptions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b bg-gray-50 text-xs text-gray-500 flex items-center justify-between sticky top-0">
                            <span>{input ? 'Search Results' : 'Popular Categories'}</span>
                            <span className="text-gray-400">{categoryOptions.length}</span>
                        </div>
                        {categoryOptions
                            .filter(cat => !value.includes(cat))
                            .map((category) => (
                                <button
                                    key={category}
                                    onClick={() => handleAdd(category)}
                                    className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm flex items-center justify-between"
                                >
                                    <span>{category}</span>
                                    <span className="text-xs text-gray-400">+</span>
                                </button>
                            ))}
                    </div>
                )}

                {/* AI Suggestions Dropdown */}
                {showSuggestions && suggestions.length > 0 && (
                    <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        <div className="p-2 border-b bg-gray-50 text-xs text-gray-500 flex items-center sticky top-0">
                            <Sparkles size={12} className="mr-1 text-purple-500" />
                            AI Suggested
                        </div>
                        {suggestions.map((suggestion, index) => (
                            <button
                                key={`${type}-suggestion-${suggestion}-${index}`}
                                onClick={() => handleAdd(suggestion)}
                                className="w-full text-left px-4 py-2 hover:bg-gray-50 text-sm"
                            >
                                {suggestion}
                            </button>
                        ))}
                    </div>
                )}
            </div>
            {type === 'keywords' && !isReviewMode && (
                <p className="text-xs text-gray-500">
                    Tip: paste or type multiple keywords separated by commas — e.g. <code>red, cotton, medium</code> — to add them all at once.
                </p>
            )}
        </div>
    );
};

export default AITagSuggest;